import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  useCharacterCount,
  useWizardFieldMessage,
  WizardFieldFooter,
  wizardFieldCountClasses,
  wizardFieldDisabledShadow,
  wizardFieldFocusRing,
  wizardFieldInvalidRing,
  wizardFieldMessageClasses,
  wizardFieldMessageSlotClasses,
  wizardFieldRestingShadow,
  wizardFieldSuccessRing,
} from "@ui/components/internal/WizardField";
import { PROSE_CLASSES } from "@ui/components/RichText/prose";
import { resolveValidationState } from "@ui/components/shared/resolveValidationState";
import { tv, type VariantProps } from "@ui/lib/tv";
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import { Toolbar } from "./Toolbar";

// The toolbar + editor box IS the pill (no inner input row), so like WizardTextArea this builds
// its own tv from the shared wizard field ring + footer constants.
export const wizardRichTextArea = tv({
  slots: {
    wrapper: "group flex w-full flex-col items-start justify-start gap-1.5",
    label: "text-base/6 text-neutral-800",
    // Toolbar + editor box. The colour ring/border live here. `overflow-hidden` clips the
    // toolbar/content corners to the radius but does NOT clip the root's own outset halo.
    root: [
      "flex w-full flex-1 flex-col overflow-hidden rounded-3xl bg-white",
      wizardFieldRestingShadow,
      "transition duration-100 ease-linear",
    ],
    content: [
      `${PROSE_CLASSES} min-h-40 flex-1 px-4 py-3 text-neutral-800 outline-none`,
      "[&_.tiptap]:min-h-40 [&_.tiptap]:outline-none",
      // Prose zeroes the margin on its own first child (`.tiptap`), but the first/last *block*
      // inside still carries prose's 1.25em paragraph margin — that stacks on `py-3` and reads as
      // an oversized gap under the toolbar / above the bottom edge. Collapse those edge margins.
      "[&_.tiptap>*:first-child]:mt-0 [&_.tiptap>*:last-child]:mb-0",
      "[&_.tiptap_p.is-editor-empty:first-child::before]:pointer-events-none",
      "[&_.tiptap_p.is-editor-empty:first-child::before]:float-left",
      "[&_.tiptap_p.is-editor-empty:first-child::before]:h-0",
      "[&_.tiptap_p.is-editor-empty:first-child::before]:text-neutral-500",
      "[&_.tiptap_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]",
    ],
    messageSlot: [wizardFieldMessageSlotClasses, "px-4"],
    message: wizardFieldMessageClasses,
    count: wizardFieldCountClasses,
  },
  variants: {
    color: { red: {}, green: {}, purple: {} },
    isFocused: { true: {} },
    isDisabled: { true: {} },
    validationState: {
      invalid: { message: "text-pink-600" },
      success: { message: "text-green-600" },
    },
    // Character count nears its limit → pink, reusing the error hue as a gentle "ease up".
    nearLimit: { true: { count: "text-pink-600" } },
  },
  compoundVariants: [
    // Validation rings reuse the focus ring's 8px+2px geometry (colour-only change) so the ring
    // never resizes on focus → valid/invalid mid-type, which would read as jitter.
    {
      validationState: "invalid",
      class: { root: wizardFieldInvalidRing },
    },
    {
      validationState: "success",
      class: { root: wizardFieldSuccessRing },
    },
    {
      isDisabled: true,
      class: { root: ["bg-neutral-25", wizardFieldDisabledShadow], content: "text-neutral-500" },
    },
    // Focus ring by colour (suppressed while a validation state owns the ring).
    {
      color: "red",
      isFocused: true,
      isDisabled: false,
      class: { root: wizardFieldFocusRing.red },
    },
    {
      color: "green",
      isFocused: true,
      isDisabled: false,
      class: { root: wizardFieldFocusRing.green },
    },
    {
      color: "purple",
      isFocused: true,
      isDisabled: false,
      class: { root: wizardFieldFocusRing.purple },
    },
  ],
  defaultVariants: {
    color: "purple",
    isFocused: false,
    isDisabled: false,
  },
});

type WizardRichTextAreaVariants = VariantProps<typeof wizardRichTextArea>;

export interface WizardRichTextAreaProps {
  label: string;
  isLabelHidden?: boolean;
  /** Rich-text HTML. */
  value: string;
  onChange: (html: string) => void;
  color?: WizardRichTextAreaVariants["color"];
  placeholder?: string;
  isInvalid?: boolean;
  isDisabled?: boolean;
  /** Validation message rendered below the field when `isInvalid`. */
  errorMessage?: ReactNode;
  /** Success message rendered below the field once it has validated clean. Takes a green ring.
   *  Suppressed while `isInvalid`. Like WizardTextArea, the field shows no trailing icon. */
  successMessage?: ReactNode;
  /** When true and a positive `maxLength` is set, renders a live `used/max` count of the editor's
   *  plain-text length (code points, paragraph breaks included), turning pink as it nears the limit. */
  showCharacterCount?: boolean;
  maxLength?: number;
  className?: string;
}

export function WizardRichTextArea({
  label,
  isLabelHidden,
  value,
  onChange,
  color = "purple",
  placeholder,
  isInvalid,
  isDisabled,
  errorMessage,
  successMessage,
  showCharacterCount,
  maxLength,
  className,
}: WizardRichTextAreaProps) {
  const labelId = useId();
  const countId = useId();
  const [isFocused, setIsFocused] = useState(false);

  // Stable references so TipTap's per-render option diff is a no-op; a fresh `extensions` array
  // every render forces `setOptions` (view.setProps/updateState) on every keystroke otherwise.
  const extensions = useMemo(
    () => [
      StarterKit.configure({ heading: false, blockquote: false, codeBlock: false, code: false, horizontalRule: false }),
      Underline,
      // Adds `is-editor-empty` + `data-placeholder` to the empty node; the content slot's
      // `::before` rule renders it. (Without this, the empty editor shows nothing.)
      Placeholder.configure({ placeholder: placeholder ?? "" }),
    ],
    [placeholder],
  );
  // Static attributes set on the contenteditable. ProseMirror gives it no implicit role, so we
  // declare `textbox`/`aria-multiline` (a rich-text field IS a multiline textbox) — improves the
  // accessible name/role and lets `getByRole("textbox")` resolve it. `aria-labelledby` is stable
  // (useId) so it lives here too; the reactive aria bits live in the effect below.
  const editorProps = useMemo(
    () => ({
      attributes: {
        role: "textbox",
        "aria-multiline": "true",
        "aria-labelledby": labelId,
        class: "min-h-40 outline-none",
      },
    }),
    [labelId],
  );

  const editor = useEditor({
    extensions,
    content: value,
    editable: !isDisabled,
    immediatelyRender: false,
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    editorProps,
  });

  const validationState = resolveValidationState(isInvalid, successMessage);
  const hasCount = Boolean(showCharacterCount) && maxLength != null && maxLength > 0;
  // Only walk the document when the count is shown. The shared hook counts by code point (spread)
  // rather than UTF-16 units so emoji and other astral characters count as one.
  const count = useCharacterCount({
    value: hasCount ? (editor?.getText() ?? "") : "",
    maxLength,
    isEnabled: hasCount,
  });
  const nearLimit = hasCount && count.nearLimit;
  const { messageId, ariaDescribedBy } = useWizardFieldMessage({
    isInvalid,
    errorMessage,
    successMessage,
    ariaDescribedBy: hasCount ? countId : undefined,
  });

  // Sync external value changes (form reset/hydrate) into the editor. We track the last value we've
  // seen so we only re-seed on a genuine *external* change — not on mount (the editor already
  // initialised from `value`) and not on the user's own edits (which would jump the caret to the end
  // and momentarily flag the last block's formatting as active). The `false` skips emitting an
  // update so we never loop back through onChange.
  const lastValue = useRef(value);
  useEffect(() => {
    if (!editor || value === lastValue.current) return;
    lastValue.current = value;
    if (value !== editor.getHTML()) editor.commands.setContent(value, false);
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(!isDisabled);
  }, [editor, isDisabled]);

  // Reactive ARIA on the actual contenteditable. (aria-labelledby is static and set via
  // editorProps.attributes; invalid/describedby change with props, so they're applied here.)
  useEffect(() => {
    const dom = editor?.view.dom;
    if (!dom) return;
    if (isInvalid) dom.setAttribute("aria-invalid", "true");
    else dom.removeAttribute("aria-invalid");
    if (ariaDescribedBy) dom.setAttribute("aria-describedby", ariaDescribedBy);
    else dom.removeAttribute("aria-describedby");
  }, [editor, isInvalid, ariaDescribedBy]);

  const styles = wizardRichTextArea({
    color,
    isFocused: isFocused && !validationState,
    isDisabled,
    validationState,
    nearLimit,
  });

  return (
    <div className={styles.wrapper({ className })}>
      <span className={isLabelHidden ? "sr-only" : styles.label()} id={labelId}>
        {label}
      </span>
      <div className={styles.root()}>
        {editor && <Toolbar color={color} editor={editor} isDisabled={isDisabled} />}
        <EditorContent className={styles.content()} editor={editor} />
      </div>
      <WizardFieldFooter
        classNames={{ messageSlot: styles.messageSlot(), message: styles.message(), count: styles.count() }}
        countId={countId}
        countText={hasCount ? count.text : undefined}
        errorMessage={errorMessage}
        isInvalid={isInvalid}
        messageId={messageId}
        successMessage={successMessage}
      />
    </div>
  );
}
